/*
 * Emergent Google Auth — session_id → session_token exchange handler.
 * Rendered when the URL contains `#session_id=…`. Exchanges the ID with the
 * backend (which sets the httpOnly cookie), refreshes the AuthProvider, and
 * navigates to the dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { authSession } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(match[1]);

    (async () => {
      try {
        await authSession(session_id);
        await refresh();
        toast.success("Signed in");
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail || "Sign-in failed";
        // 403 = whitelist rejection. Surface it prominently and stay on this
        // page for a moment so the user can read it instead of a fleeting toast.
        if (status === 403) {
          toast.error(`Access denied: ${detail}. Ask the app owner to add your email to the allowlist.`, {
            duration: 12000,
          });
        } else {
          toast.error(detail);
        }
      } finally {
        // Clear the fragment and navigate to the dashboard root
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, refresh]);

  return (
    <div
      className="min-h-screen bg-black flex items-center justify-center"
      data-testid="auth-callback"
    >
      <div className="text-zinc-400 font-mono text-sm">
        signing you in…
      </div>
    </div>
  );
}
